import type { PoiSummary } from '@linktivity/tata';
import { action, computed, observable, runInAction } from 'mobx';
import type { POI } from '~/layouts/Base/types';
import { usersApi } from '~/services';
import { poiSummaryToPoi, toGooglePlaceId } from '~/utils/poi';

// Nearby summaries can carry a synthetic place id (see placePoisToPoiSummaries),
// so everything here keys on the google id the API actually stores.
function keyOf(summary: PoiSummary): string | undefined {
  return toGooglePlaceId(summary.place?.id);
}

// The count and the bookmarks list both derive from this array, so drop
// anything only one of them could represent: summaries without a place id
// (poiSummaryToPoi rejects those) and repeats of a place already kept.
function normalize(summaries: PoiSummary[]): PoiSummary[] {
  const seen = new Set<string>();
  return summaries.filter(summary => {
    const key = keyOf(summary);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export class FavouritesStore {
  // The POIs the user has bookmarked — the single source for the bookmark
  // icons, the count on the profile page and the cards on the bookmarks page.
  @observable accessor favouritePOIs: PoiSummary[] = [];
  // Set when the server had more favourites than it returned. The generated
  // client has no page parameter, so the list (and the count) is capped at
  // the first page until the API grows one.
  @observable accessor hasMore = false;
  // Whether a load has settled, so the bookmarks page can tell "no bookmarks"
  // apart from "not fetched yet" instead of flashing its empty state.
  @observable accessor loaded = false;

  private pending: Promise<void> | null = null;
  // Track which POI changed at each version so a load can merge only the
  // optimistic changes made while its server snapshot was in flight.
  private mutationVersion = 0;
  private mutationVersions = new Map<string, number>();
  private pendingMutations = new Map<string, number>();
  private mutationQueues = new Map<string, Promise<void>>();

  @computed
  get pois(): POI[] {
    return this.favouritePOIs
      .map(poiSummaryToPoi)
      .filter((poi): poi is POI => poi !== null);
  }

  @computed
  get placeIds(): Set<string> {
    const ids = this.favouritePOIs
      .map(keyOf)
      .filter((id): id is string => !!id);
    return new Set(ids);
  }

  // Deliberately the length of the rendered list rather than a separate
  // tally: the profile count can then never disagree with the cards.
  get count(): number {
    return this.pois.length;
  }

  isFavourited(placeId: string): boolean {
    return this.placeIds.has(placeId);
  }

  // Idempotent: reuses the in-flight or already-finished load.
  load(): Promise<void> {
    if (this.pending) return this.pending;
    if (this.loaded) return Promise.resolve();
    return this.fetch();
  }

  // Pages that show favourites call this on mount — the list would otherwise
  // stay as first loaded for the whole session, even if the user changed
  // their bookmarks elsewhere. Joins an in-flight load instead of firing a
  // second identical request.
  reload(): Promise<void> {
    return this.pending ?? this.fetch();
  }

  private fetch(): Promise<void> {
    const version = this.mutationVersion;
    const pendingAtStart = new Set(this.pendingMutations.keys());
    const request = usersApi
      .listUserFavouritePois()
      .then(res => {
        if (res.hasMore) {
          console.warn(
            '[favourite pois] server has more favourites than it returned; the count is a lower bound'
          );
        }
        runInAction(() => {
          let favourites = normalize(res.pois ?? []);

          // Preserve local changes made after this request started. Also
          // preserve mutations that were already pending at request time:
          // their API write may not have reached the server snapshot yet.
          for (const [placeId, mutationVersion] of this.mutationVersions) {
            if (mutationVersion <= version && !pendingAtStart.has(placeId)) {
              continue;
            }
            const localSummary = this.favouritePOIs.find(
              summary => keyOf(summary) === placeId
            );
            favourites = favourites.filter(
              summary => keyOf(summary) !== placeId
            );
            if (localSummary) favourites.push(localSummary);
          }

          this.favouritePOIs = favourites;
          this.hasMore = res.hasMore ?? false;
        });
      })
      .catch(error => {
        console.error('Failed to load favourite POIs', error);
      })
      .finally(() => {
        runInAction(() => {
          this.loaded = true;
        });
        if (this.pending === request) this.pending = null;
      });
    this.pending = request;
    return request;
  }

  @action
  private add(summary: PoiSummary) {
    const key = keyOf(summary);
    if (!key || this.placeIds.has(key)) return;
    this.favouritePOIs = [...this.favouritePOIs, summary];
  }

  @action
  private remove(placeId: string) {
    this.favouritePOIs = this.favouritePOIs.filter(
      summary => keyOf(summary) !== placeId
    );
  }

  private recordMutation(placeId: string): number {
    this.mutationVersion += 1;
    this.mutationVersions.set(placeId, this.mutationVersion);
    return this.mutationVersion;
  }

  private beginMutation(placeId: string) {
    this.pendingMutations.set(
      placeId,
      (this.pendingMutations.get(placeId) ?? 0) + 1
    );
  }

  private endMutation(placeId: string) {
    const remaining = (this.pendingMutations.get(placeId) ?? 1) - 1;
    if (remaining > 0) this.pendingMutations.set(placeId, remaining);
    else this.pendingMutations.delete(placeId);
  }

  // Optimistically updates the list, then reconciles with the API and undoes
  // the change on failure so the button doesn't lie about server state.
  // `summary` is what the bookmarks page renders for a newly added POI;
  // without it the POI only gains its details on the next load.
  async toggleFavourite(placeId: string, summary?: PoiSummary) {
    const wasFavourited = this.isFavourited(placeId);
    // Kept so a failed removal can restore the exact entry.
    const removed = this.favouritePOIs.find(poi => keyOf(poi) === placeId);

    if (wasFavourited) this.remove(placeId);
    else this.add(summary ?? { place: { id: placeId } });
    const operationVersion = this.recordMutation(placeId);
    this.beginMutation(placeId);

    // Keep writes for the same POI ordered. Without this, a fast
    // favourite/unfavourite pair can reach the server in reverse order.
    const previous = this.mutationQueues.get(placeId) ?? Promise.resolve();
    const operation = previous.then(async () => {
      try {
        if (wasFavourited) {
          await usersApi.removeUserFavouritePoi({ placeId });
        } else {
          await usersApi.addUserFavouritePoi({ body: { placeId } });
        }
      } catch (error) {
        console.error('Failed to update favourite POI', error);

        // A newer click already owns the visible state, so an older failed
        // request must not roll it back.
        if (this.mutationVersions.get(placeId) === operationVersion) {
          if (removed) this.add(removed);
          else this.remove(placeId);
          this.recordMutation(placeId);
        }
      } finally {
        this.endMutation(placeId);
      }
    });
    this.mutationQueues.set(placeId, operation);

    await operation;
    if (this.mutationQueues.get(placeId) === operation) {
      this.mutationQueues.delete(placeId);
    }
  }
}

export default new FavouritesStore();
